import React, { useEffect, useState } from 'react';
import axios from 'axios';

export default function App() {
    const API = '/api';

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    const [token, setToken] = useState(localStorage.getItem('ra_token') || '');
    const [me, setMe] = useState(null);
    const [status, setStatus] = useState('');

    const [recording, setRecording] = useState('');
    const [title, setTitle] = useState('');
    const [voiceFile, setVoiceFile] = useState(null);

    const [message, setMessage] = useState('');
    const [chatResponse, setChatResponse] = useState('');

    const authed = axios.create({
        headers: token ? { Authorization: `Bearer ${token}` } : {}
    });

    const refreshMe = async () => {
        if (!token) return setMe(null);
        try {
            const res = await authed.get(`${API}/me`);
            setMe(res.data);
        } catch (e) {
            setMe(null);
            setToken('');
            localStorage.removeItem('ra_token');
        }
    };

    useEffect(() => { refreshMe(); }, [token]);

    const register = async () => {
        setStatus('');
        const res = await axios.post(`${API}/register`, { email, password });
        if (res.data.ok) {
            setStatus('Registered. Check your email to verify, then login.');
        } else {
            setStatus(res.data.error || 'Error');
        }
    };

    const login = async () => {
        setStatus('');
        const res = await axios.post(`${API}/login`, { email, password });
        if (res.data.ok && res.data.token) {
            setToken(res.data.token);
            localStorage.setItem('ra_token', res.data.token);
            setStatus('Logged in.');
        } else {
            setStatus(res.data.error || 'Error');
        }
    };

    const resendVerification = async () => {
        setStatus('');
        const res = await axios.post(`${API}/resend-verification`, { email });
        setStatus(res.data.ok ? 'Verification email sent.' : (res.data.error || 'Error'));
    };

    const subscribe = async (plan) => {
        setStatus('');
        try {
            const res = await authed.post(`${API}/billing/create-checkout-session`, { plan });
            if (res.data.url) window.location.href = res.data.url;
            else setStatus(res.data.error || 'No checkout URL returned');
        } catch (e) {
            setStatus(e?.response?.data?.error || 'Failed to start checkout');
        }
    };

    const saveResponse = async () => {
        setStatus('');
        try {
            const res = await authed.post(`${API}/save-response`, { title, content: recording });
            setStatus(res.data.ok ? 'Saved!' : (res.data.error || 'Error'));
        } catch (e) {
            setStatus(e?.response?.data?.error || 'Error saving');
        }
    };

    const uploadAudio = async () => {
        setStatus('');
        if (!voiceFile) return setStatus('Pick an audio file first');
        const form = new FormData();
        form.append('file', voiceFile);

        try {
            const res = await authed.post(`${API}/upload-audio`, form, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
            });
            setStatus(res.data.ok ? `Uploaded: ${res.data.filename}` : (res.data.error || 'Error'));
        } catch (e) {
            setStatus(e?.response?.data?.error || 'Upload failed');
        }
    };

    const sendMessage = async () => {
        setStatus('');
        try {
            const res = await authed.post(`${API}/chat`, { message });
            setChatResponse(res.data.reply || res.data.error || '');
        } catch (e) {
            const data = e?.response?.data;
            if (data?.needs_subscription) {
                setChatResponse(`Chat requires the $20/mo plan. Current tier: ${data.current_tier}`);
            } else {
                setChatResponse(data?.error || 'Chat error');
            }
        }
    };

    const logout = () => {
        setToken('');
        localStorage.removeItem('ra_token');
        setMe(null);
        setStatus('Logged out.');
    };

    const tier = me?.tier || 'free';
    const verified = !!me?.email_verified;

    return (
        <div className="p-4 max-w-xl mx-auto text-white bg-gray-900 min-h-screen">
            <h1 className="text-2xl font-bold mb-4">RememberAnce AI</h1>

            {!token ? (
                <>
                    <input className="w-full p-2 mb-2 bg-gray-800 rounded" placeholder="Email" value={email}
                        onChange={e => setEmail(e.target.value)} />
                    <input className="w-full p-2 mb-4 bg-gray-800 rounded" placeholder="Password" type="password"
                        value={password} onChange={e => setPassword(e.target.value)} />

                    <div className="flex gap-2 mb-4">
                        <button className="bg-blue-600 px-4 py-2 rounded" onClick={register}>Register</button>
                        <button className="bg-green-600 px-4 py-2 rounded" onClick={login}>Login</button>
                    </div>

                    <button className="text-sm underline text-gray-300" onClick={resendVerification}>
                        Resend verification email
                    </button>
                </>
            ) : (
                <>
                    <div className="mb-4 text-sm">
                        <div>Signed in as: <span className="text-yellow-300">{me?.email}</span></div>
                        <div>Email verified: <span className={verified ? 'text-green-300' : 'text-red-300'}>{String(verified)}</span></div>
                        <div>Tier: <span className="text-purple-300">{tier}</span></div>
                        <button className="mt-2 bg-gray-700 px-3 py-1 rounded" onClick={logout}>Logout</button>
                    </div>

                    {!verified && (
                        <div className="bg-red-900/40 border border-red-700 p-3 rounded mb-4">
                            Please verify your email before subscribing/using features.
                            <div>
                                <button className="mt-2 underline" onClick={resendVerification}>Resend verification</button>
                            </div>
                        </div>
                    )}

                    {verified && tier === 'free' && (
                        <div className="bg-gray-800 border border-gray-700 p-3 rounded mb-6">
                            <div className="font-semibold mb-2">Unlock features</div>
                            <div className="flex gap-2">
                                <button className="bg-yellow-600 px-4 py-2 rounded" onClick={() => subscribe('recorder')}>
                                    Recorder $5/mo (record + upload)
                                </button>
                                <button className="bg-pink-600 px-4 py-2 rounded" onClick={() => subscribe('chat')}>
                                    Chat $20/mo (full access)
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {status && <div className="my-4 text-sm text-gray-200">{status}</div>}

            <h2 className="text-lg font-semibold mb-2">Save a Memory</h2>
            <input className="w-full p-2 mb-2 bg-gray-800 rounded" placeholder="Optional title"
                value={title} onChange={e => setTitle(e.target.value)} />
            <textarea className="w-full p-2 mb-2 bg-gray-800 rounded" placeholder="Write a memory..."
                value={recording} onChange={e => setRecording(e.target.value)} />
            <button className="bg-purple-600 px-4 py-2 mb-6 rounded" onClick={saveResponse}>
                Save Memory
            </button>

            <h2 className="text-lg font-semibold mb-2">Upload Voice</h2>
            <input type="file" className="mb-2" onChange={e => setVoiceFile(e.target.files[0])} />
            <button className="bg-yellow-600 px-4 py-2 mb-6 rounded" onClick={uploadAudio}>
                Upload Audio
            </button>

            <h2 className="text-lg font-semibold mb-2">Chat</h2>
            <input className="w-full p-2 mb-2 bg-gray-800 rounded" placeholder="Ask something..."
                value={message} onChange={e => setMessage(e.target.value)} />
            <button className="bg-pink-600 px-4 py-2 mb-4 rounded" onClick={sendMessage}>
                Ask
            </button>

            {chatResponse && (
                <div className="bg-gray-700 p-4 rounded whitespace-pre-wrap">
                    {chatResponse}
                </div>
            )}
        </div>
    );
}
